import { useState } from "react";
import Reveal from "../Reveal";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "What does Legacy Studio actually do?",
    a: "Legacy Studio is a full-stack marketing studio led by Mark Cassara out of Celina, Texas with national operations. We handle SEO, paid media (Meta, Google, TikTok, YouTube), social media, content, branding & web, agentic customer/tech support powered by AI, and full-stack virtual assistants — all under one roof.",
  },
  {
    q: "Where are you located and who do you work with?",
    a: "Our studio is based in Celina, TX, but we run engagements nationwide across the United States. We work with operators in healthcare, real estate, finance, education, events, legal services, hyperbaric and oxygen therapy, and DTC brands.",
  },
  {
    q: "How is Legacy Studio different from a typical marketing agency?",
    a: "Three differences: (1) We're operator-led — Mark and his senior team have run brands themselves, so judgement isn't outsourced to junior account managers. (2) We build sustainable systems — playbooks, SOPs and AI agents your team can run after the engagement. (3) We're full-stack, so SEO, paid, content, support and ops VAs all sit on the same roadmap instead of fighting for budget across three vendors.",
  },
  {
    q: "What is agentic customer and tech support?",
    a: "Agentic support is AI-powered customer service. We deploy AI agents trained on your brand voice, product docs and SOPs to handle tier-1 customer and technical support 24/7, escalating only when human judgement is required. It dramatically reduces support cost while improving response time.",
  },
  {
    q: "What are full-stack VA services?",
    a: "Vetted virtual assistants and project managers we embed into your operations — covering inbox triage, calendar, light bookkeeping, CRM hygiene, vendor follow-ups and project management. Hire one role or a stack, scaled by hours per week.",
  },
  {
    q: "What do engagements typically cost?",
    a: "Most retainers fall between $5k and $25k+ per month depending on channel mix, ad spend managed, and the size of the VA / agentic team. We also run one-off branding and website projects. Tell us your goals on the contact form and we'll come back with a scoped proposal.",
  },
  {
    q: "How do I start a project?",
    a: "Use the 'Start a project' button anywhere on this page or email Mark directly at Mark@legacystudio.cc. We reply to every message within 1 business day with either a proposal outline or a 25-minute discovery call.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  // JSON-LD for AEO / Answer-Engine-Optimization
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 border-t border-[#2E2E38] section-stripe"
    >
      {/* Inline JSON-LD so AI search engines and Google can parse Q&A */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
            <div className="md:col-span-4">
              <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.25em] text-[#E3FF34]">
                [ FAQ ]
              </p>
            </div>
            <div className="md:col-span-8">
              <h2
                data-testid="faq-heading"
                className="font-['Outfit'] font-medium tracking-tight text-4xl md:text-5xl lg:text-6xl text-[#F2F2F2]"
              >
                Answers,{" "}
                <span className="text-[#A3A3A3]">before you ask.</span>
              </h2>
            </div>
          </div>
        </Reveal>

        <div className="border-t border-[#2E2E38] surface-card-flat">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.04}>
                <div
                  data-testid={`faq-item-${i}`}
                  className={`border-b border-[#2E2E38] transition-colors duration-300 ${
                    isOpen ? "bg-[#16161E]" : "hover:bg-[#13131A]"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 md:py-8 text-left group"
                  >
                    <h3 className="font-['Outfit'] text-xl md:text-2xl tracking-tight text-[#F2F2F2] group-hover:text-[#E3FF34] transition-colors">
                      {f.q}
                    </h3>
                    <span
                      className={`flex-none w-9 h-9 inline-flex items-center justify-center border border-[#2E2E38] text-[#F2F2F2] transition-all duration-500 ${
                        isOpen
                          ? "rotate-45 bg-[#E3FF34] text-[#050505] border-[#E3FF34]"
                          : "group-hover:border-[#E3FF34]/50"
                      }`}
                    >
                      <Plus size={16} />
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-500 ease-out px-6 md:px-8 ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100 pb-6 md:pb-8"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="max-w-3xl font-['Outfit'] font-light text-[#A3A3A3] text-base md:text-lg leading-relaxed">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
